import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { applyPredictiveStyleCodeFallback, looksLikeStyleCode } from '@theme/cw-style-code-search';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  static #collectionVisibility = new Map();

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    if (window.matchMedia('(max-width: 749px)').matches) return;

    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('type', 'product');
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  static #SEARCHANISE_API_KEY = '3a1G3m7Q2L';
  static #SEARCHANISE_HOST = 'https://searchserverapi1.com';

  /**
   * Fetch Searchanise results, render theme product cards, inject suggestions/collections/pages.
   * Falls back to native Shopify predictive search if Searchanise fails.
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const abortController = this.#createAbortController();
    const { predictiveSearchResults } = this.refs;

    const snizeData = await this.#fetchSearchaniseData(searchTerm, abortController.signal);

    if (abortController.signal.aborted) return;

    const productIds = (snizeData?.items || [])
      .map((item) => item.product_id || item.id)
      .filter(Boolean);
    const hasSuggestions = Array.isArray(snizeData?.suggestions) && snizeData.suggestions.length > 0;
    const visibleCategories = await this.#visibleSearchaniseCollections(
      Array.isArray(snizeData?.categories) ? snizeData.categories : [],
      abortController.signal
    );
    const hasCategories = visibleCategories.length > 0;
    const hasPages =
      Array.isArray(snizeData?.pages) &&
      snizeData.pages.some((page) => page?.link && !/\/blogs\//i.test(String(page.link)));
    const hasExtras = hasSuggestions || hasCategories || hasPages;

    if (productIds.length === 0 && !hasExtras) {
      await this.#nativePredictiveSearch(searchTerm, abortController);
      return;
    }

    if (productIds.length > 0) {
      const idQuery = productIds.map((id) => `id:${id}`).join(' OR ');
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', idQuery);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');

      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (abortController.signal.aborted || !resultsMarkup) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#normalizeSearchaniseProductBlock(predictiveSearchResults);
    } else {
      predictiveSearchResults.innerHTML = `
        <div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" data-transition-container>
          <div class="predictive-search-results__inner" data-search-results></div>
        </div>
      `;
    }

    this.#injectSearchaniseExtras(predictiveSearchResults, snizeData, searchTerm, visibleCategories);
    this.#resetScrollPositions();
  }

  /**
   * @param {string} query
   * @param {AbortSignal} signal
   * @returns {Promise<object | null>}
   */
  async #fetchSearchaniseData(query, signal) {
    try {
      const url = new URL(`${PredictiveSearchComponent.#SEARCHANISE_HOST}/getwidgets`);
      url.searchParams.set('api_key', PredictiveSearchComponent.#SEARCHANISE_API_KEY);
      url.searchParams.set('q', query);
      url.searchParams.set('output', 'json');
      url.searchParams.set('items', 'true');
      url.searchParams.set('pages', 'true');
      url.searchParams.set('categories', 'true');
      url.searchParams.set('suggestions', 'true');
      url.searchParams.set('maxResults', '12');

      const res = await fetch(url.toString(), { signal });
      if (!res.ok) return null;

      return await res.json();
    } catch {
      return null;
    }
  }

  /**
   * Convert recently-viewed shell (from id: query) into normal Products block.
   * @param {HTMLElement} container
   */
  #normalizeSearchaniseProductBlock(container) {
    const wrapper = container.querySelector('.recently-viewed-wrapper');
    if (!wrapper) return;

    wrapper.classList.remove('recently-viewed-wrapper');
    wrapper.removeAttribute('ref');

    const title = wrapper.querySelector('.predictive-search-results__title');
    if (title) {
      title.textContent = 'Products';
      title.removeAttribute('ref');
    }

    wrapper.querySelector('.predictive-search-results__clear')?.remove();

    wrapper.querySelectorAll('[ref="recentlyViewedItems[]"]').forEach((item) => {
      item.setAttribute('ref', 'resultsItems[]');
    });
  }

  /**
   * Inject Searchanise suggestions, collections, and pages into theme UI.
   * @param {HTMLElement} container
   * @param {object | null} data
   * @param {string} searchTerm
   * @param {Array<{title?: string, link?: string}>} [visibleCategories]
   */
  #injectSearchaniseExtras(container, data, searchTerm, visibleCategories = []) {
    if (!data) return;

    const resultsInner =
      container.querySelector('[data-search-results]') ||
      container.querySelector('.predictive-search-results__inner') ||
      container;

    container.querySelector('[data-cw-searchanise-extras]')?.remove();
    resultsInner.removeAttribute('data-cw-searchanise-layout');

    const extras = document.createElement('aside');
    extras.setAttribute('data-cw-searchanise-extras', '');
    extras.className = 'cw-searchanise-sidebar';

    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const suggestionLimit = isMobile ? 3 : 8;
    const categoryLimit = isMobile ? 3 : 8;
    const pageLimit = isMobile ? 3 : 8;

    const suggestions = Array.isArray(data.suggestions) ? data.suggestions.slice(0, suggestionLimit) : [];
    if (suggestions.length > 0) {
      const section = document.createElement('div');
      section.className = 'cw-searchanise-resource-list';

      const header = document.createElement('div');
      header.className = 'predictive-search-results__resource-header';
      header.innerHTML = `<h4 class="predictive-search-results__title">Suggestions</h4>`;
      section.appendChild(header);

      const ul = document.createElement('ul');
      ul.className =
        'predictive-search-results__list predictive-search-results__wrapper cw-searchanise-resource-list__items list-unstyled';

      for (const suggestion of suggestions) {
        const text = typeof suggestion === 'string' ? suggestion : suggestion?.text || suggestion?.title;
        if (!text) continue;

        const href = `${Theme.routes.search_url}?type=product&q=${encodeURIComponent(text)}`;
        const li = document.createElement('li');
        li.className = 'predictive-search-results__card--query';
        li.setAttribute('ref', 'resultsItems[]');
        li.innerHTML = `
          <a class="cw-searchanise-sidebar__link" href="${href}" aria-label="${this.#escapeHtml(text)}">
            <span>${this.#highlightSuggestion(text, searchTerm)}</span>
          </a>
        `;
        ul.appendChild(li);
      }

      if (ul.children.length) {
        section.appendChild(ul);
        extras.appendChild(section);
      }
    }

    const categories = visibleCategories.slice(0, categoryLimit);
    if (categories.length > 0) {
      extras.appendChild(this.#buildResourceList('Categories', categories));
    }

    const pages = (Array.isArray(data.pages) ? data.pages : [])
      .filter((page) => {
        const link = String(page?.link || '');
        return link && !/\/blogs\//i.test(link);
      })
      .slice(0, pageLimit);
    if (pages.length > 0) {
      extras.appendChild(this.#buildResourceList('Pages', pages));
    }

    if (!extras.children.length) return;

    const productsBlock = resultsInner.querySelector('#predictive-search-products');
    if (productsBlock) {
      resultsInner.insertBefore(extras, productsBlock);
      resultsInner.setAttribute('data-cw-searchanise-layout', 'split');
    } else {
      resultsInner.prepend(extras);
    }
  }

  /**
   * Keep collections that are published on the Online Store and not marked DELETE.
   * Searchanise still indexes unpublished collections (e.g. /collections/travel-1).
   * @param {Array<{title?: string, link?: string}>} categories
   * @param {AbortSignal} [signal]
   * @returns {Promise<Array<{title?: string, link?: string}>>}
   */
  async #visibleSearchaniseCollections(categories, signal) {
    const published = new Set(
      (typeof Theme !== 'undefined' && Array.isArray(Theme.publishedCollectionHandles)
        ? Theme.publishedCollectionHandles
        : []
      ).map((handle) => String(handle).toLowerCase())
    );
    const hidden = new Set(
      (typeof Theme !== 'undefined' && Array.isArray(Theme.hiddenCollectionHandles)
        ? Theme.hiddenCollectionHandles
        : []
      ).map((handle) => String(handle).toLowerCase())
    );
    const seen = new Set();
    const visible = [];
    const unknown = [];

    for (const category of categories) {
      const title = String(category?.title || '').trim();
      const link = String(category?.link || '').trim();
      if (!title || !link) continue;
      if (/\bdelete\b/i.test(title)) continue;

      const handle = this.#collectionHandleFromLink(link);
      if (!handle || handle.includes('delete') || hidden.has(handle) || seen.has(handle)) continue;

      if (published.has(handle)) {
        seen.add(handle);
        visible.push(category);
        continue;
      }

      unknown.push({ category, handle });
    }

    if (unknown.length === 0) return visible;

    const checked = await Promise.all(
      unknown.map(async ({ category, handle }) => {
        const isLive = await this.#isStorefrontCollection(handle, signal);
        return isLive ? category : null;
      })
    );

    for (const category of checked) {
      if (!category) continue;
      const handle = this.#collectionHandleFromLink(category.link);
      if (!handle || seen.has(handle)) continue;
      seen.add(handle);
      visible.push(category);
    }

    return visible;
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} [signal]
   * @returns {Promise<boolean>}
   */
  async #isStorefrontCollection(handle, signal) {
    const cached = PredictiveSearchComponent.#collectionVisibility.get(handle);
    if (typeof cached === 'boolean') return cached;

    try {
      const res = await fetch(`/collections/${encodeURIComponent(handle)}`, {
        method: 'GET',
        signal,
        credentials: 'same-origin',
      });
      const ok = res.ok && /\/collections\//i.test(res.url);
      PredictiveSearchComponent.#collectionVisibility.set(handle, ok);
      return ok;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      PredictiveSearchComponent.#collectionVisibility.set(handle, false);
      return false;
    }
  }

  /**
   * @param {string} link
   * @returns {string}
   */
  #collectionHandleFromLink(link) {
    const path = String(link || '').split('?')[0];
    const match = path.match(/\/collections\/([^/]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  }

  /**
   * @param {string} title
   * @param {Array<{title?: string, link?: string}>} resources
   * @returns {HTMLElement}
   */
  #buildResourceList(title, resources) {
    const section = document.createElement('div');
    section.className = 'cw-searchanise-resource-list';

    const header = document.createElement('div');
    header.className = 'predictive-search-results__resource-header';
    header.innerHTML = `<h4 class="predictive-search-results__title">${this.#escapeHtml(title)}</h4>`;
    section.appendChild(header);

    const ul = document.createElement('ul');
    ul.className =
      'predictive-search-results__list predictive-search-results__wrapper cw-searchanise-resource-list__items list-unstyled';

    for (const resource of resources) {
      const text = resource.title;
      const link = resource.link;
      if (!text || !link) continue;

      const li = document.createElement('li');
      li.className = 'predictive-search-results__card--query';
      li.setAttribute('ref', 'resultsItems[]');
      li.innerHTML = `
        <a class="cw-searchanise-sidebar__link" href="${this.#escapeHtml(link)}" aria-label="${this.#escapeHtml(text)}">
          <span>${this.#escapeHtml(text)}</span>
        </a>
      `;
      ul.appendChild(li);
    }

    section.appendChild(ul);
    return section;
  }

  /**
   * @param {string} text
   * @param {string} query
   * @returns {string}
   */
  #highlightSuggestion(text, query) {
    const safeText = this.#escapeHtml(text);
    const safeQuery = this.#escapeHtml(query.trim());
    if (!safeQuery) return safeText;

    const escaped = safeQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = safeText.match(new RegExp(escaped, 'i'));
    if (!match || match.index == null) return safeText;

    const start = match.index;
    const end = start + match[0].length;
    const before = safeText.slice(0, start);
    const matched = safeText.slice(start, end);
    const after = safeText.slice(end);

    return `${before ? `<mark>${before}</mark>` : ''}${matched}${after ? `<mark>${after}</mark>` : ''}`;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Native Shopify predictive search fallback.
   * @param {string} searchTerm
   * @param {AbortController} abortController
   */
  async #nativePredictiveSearch(searchTerm, abortController) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('options[prefix]', 'last');

    const { predictiveSearchResults } = this.refs;

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#resetScrollPositions();

        const hasNativeProducts = predictiveSearchResults.querySelector(
          '[data-predictive-native-products] .predictive-search-results__card--product'
        );

        if (looksLikeStyleCode(searchTerm)) {
          const fullSearchApplied = await this.#applyFullSearchFallback(
            searchTerm,
            predictiveSearchResults,
            abortController.signal
          );

          if (fullSearchApplied) {
            predictiveSearchResults.querySelector('[data-predictive-native-products]')?.remove();
            predictiveSearchResults.querySelector('.predictive-search-results__no-results')?.remove();
            predictiveSearchResults.querySelector('[data-cw-similar-products]')?.remove();
            return;
          }

          if (!hasNativeProducts) {
            const applied = await applyPredictiveStyleCodeFallback(searchTerm, predictiveSearchResults, {
              signal: abortController.signal,
            });

            if (applied) {
              predictiveSearchResults.querySelector('.predictive-search-results__no-results')?.remove();
              predictiveSearchResults.querySelector('[data-cw-similar-products]')?.remove();
              return;
            }
          }
        }

        if (!hasNativeProducts) {
          const applied = await this.#applyFullSearchFallback(
            searchTerm,
            predictiveSearchResults,
            abortController.signal
          );

          if (applied) {
            predictiveSearchResults.querySelector('.predictive-search-results__no-results')?.remove();
            predictiveSearchResults.querySelector('[data-cw-similar-products]')?.remove();
          }
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * When predictive search returns no products, use the same full search index as the search page.
   * @param {string} searchTerm
   * @param {HTMLElement} predictiveSearchResults
   * @param {AbortSignal} signal
   * @returns {Promise<boolean>}
   */
  async #applyFullSearchFallback(searchTerm, predictiveSearchResults, signal) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('options[prefix]', 'last');

    const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    if (signal.aborted || !markup) return false;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const fallback = parsed.querySelector('[data-predictive-full-search-fallback]');
    if (!fallback?.querySelector('.predictive-search-results__card--product')) return false;

    const resultsInner =
      predictiveSearchResults.querySelector('[data-search-results]') ||
      predictiveSearchResults.querySelector('.predictive-search-results__inner');

    if (!resultsInner) return false;

    resultsInner.prepend(fallback);
    this.#resetScrollPositions();
    return true;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const emptySectionId = 'predictive-search-empty';

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const hasCategories = Boolean(
      parsedEmptySectionMarkup.querySelector('[data-cw-search-empty-categories]')
    );

    if (!hasCategories) {
      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        if (!recentlyViewedProductsHtml) return;

        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('ref', 'recentlyViewedWrapper');
          }
        }

        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (!collectionElement) return;
        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
